import { getDbNumberForGrouping } from '../../../lib/formatUtils';

/**
 * Derive the Add Asset smart-form pre-fill context from a drill-down group's
 * assets. Pure so it can be unit-tested without rendering the session view.
 *
 * Rules:
 *  - representative asset = the group's distribution board, else the first item.
 *  - building/room come from the first item that actually carries them (a board
 *    can be missing location while its circuits have it).
 *  - db_number is ONLY populated for rcd / distribution_board groups. It is a
 *    data field only those types carry, and getDbNumberForGrouping also derives
 *    a value from equipment_code (e.g. "EL" from "EL-12"), so populating it for
 *    an exit-light group would leak a bogus db_number. Empty is recoverable; a
 *    wrong value silently corrupts grouping and reports.
 *  - defaultAssetType: a new asset in a DB group is a circuit (rcd), not another
 *    board; otherwise mirror the group's own asset type.
 *  - manufacturer/model prefer the last asset added this session, then the
 *    representative asset.
 *
 * @param {Array<object>} items    assets in the active group
 * @param {object|null}   lastAdded the last asset created this session
 * @returns {{ boardContext: object, defaultAssetType: string }}
 */
export function deriveAddAssetBoardContext(items, lastAdded = null) {
  const list = Array.isArray(items) ? items : [];
  const representativeAsset =
    list.find((a) => (a.asset_type || '').toLowerCase() === 'distribution_board')
    || list[0]
    || null;
  const itemWithLocation = list.find((a) => a.building || a.room) || representativeAsset;
  const repType = (representativeAsset?.asset_type || '').toLowerCase();
  const isDbRcdGroup = repType === 'distribution_board' || repType === 'rcd';
  const dbNumber = isDbRcdGroup ? (getDbNumberForGrouping(representativeAsset) || '') : '';
  const defaultAssetType = repType === 'distribution_board' ? 'rcd' : repType;

  return {
    defaultAssetType,
    boardContext: {
      dbNumber,
      building: itemWithLocation?.building ?? '',
      room: itemWithLocation?.room ?? '',
      manufacturer: lastAdded?.manufacturer ?? representativeAsset?.manufacturer ?? '',
      model: lastAdded?.model ?? representativeAsset?.model ?? '',
    },
  };
}
